/**
 * useVillainsLibrary — 2026-06-08 §3.E wire-up for the canonical Villains
 * Supplement VG35011 named NPCs.
 *
 * Mirrors useCanonicalNpcsLibrary.ts pattern: 3-state union + cancelled
 * flag unmount safety + memoised service stabilisation + 3-tab state.
 *
 * Tab mapping (per locked §3.A category-design decision):
 *   - 'major_villain'      → category=major_villain (7 entries)
 *   - 'lieutenant'         → category IN { major_villain_lieutenant,
 *                            major_villain_lieutenant_prose_only } (9 entries)
 *   - 'smersh'             → category IN { smersh_leader, smersh_agent } (10)
 *
 * The drawer filters the npcs array by tab → category(ies) at render time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VillainsNpcManifestEntry } from '../services/host/VillainsServiceModule';
import { VillainsServiceModule } from '../services/host/VillainsServiceModule';

export type VillainsLibraryState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type VillainsLibraryTab =
  | 'major_villain'
  | 'lieutenant'
  | 'smersh';

export interface VillainsLibraryHookResult {
  state: VillainsLibraryState;
  npcs: VillainsNpcManifestEntry[];
  pendingRemainingCount: number;
  error: string | null;
  refresh: () => void;
  activeTab: VillainsLibraryTab;
  setActiveTab: (tab: VillainsLibraryTab) => void;
  selectedNpcId: string | null;
  selectedNpcData: Record<string, unknown> | null;
  selectedNpcState: VillainsLibraryState;
  selectNpc: (id: string | null) => void;
}

export function useVillainsLibrary(
  villainsService?: VillainsServiceModule,
): VillainsLibraryHookResult {
  const resolvedService = useMemo(
    () => villainsService ?? new VillainsServiceModule(),
    [villainsService],
  );

  const [state, setState] = useState<VillainsLibraryState>('idle');
  const [npcs, setNpcs] = useState<VillainsNpcManifestEntry[]>([]);
  const [pendingRemainingCount, setPendingRemainingCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] =
    useState<VillainsLibraryTab>('major_villain');

  const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
  const [selectedNpcData, setSelectedNpcData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedNpcState, setSelectedNpcState] =
    useState<VillainsLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectNpc = useCallback((id: string | null) => {
    setSelectedNpcId(id);
    if (id === null) {
      setSelectedNpcData(null);
      setSelectedNpcState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setNpcs(result.npcs);
        setPendingRemainingCount(result.pending_remaining_count);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setNpcs([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedNpcId === null) {
      return;
    }
    let cancelled = false;

    setSelectedNpcState('loading');
    setSelectedNpcData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedNpcId);
        if (cancelled) return;
        if (result === null) {
          setSelectedNpcState('error');
          return;
        }
        setSelectedNpcData(result);
        setSelectedNpcState('ready');
      } catch {
        if (cancelled) return;
        setSelectedNpcState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedNpcId]);

  return {
    state,
    npcs,
    pendingRemainingCount,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedNpcId,
    selectedNpcData,
    selectedNpcState,
    selectNpc,
  };
}
